import { Editor } from '@tiptap/core'
import { useEditorState } from '@tiptap/react'
import { useState, useRef, FormEvent, VFC, useLayoutEffect } from 'react'
import { useLinkFormatter, useTheme } from '@opallabs/hooks'
import { isUrl } from '@opallabs/lambda'
import {
  TextInputWithLabel,
  Button,
  TextInputType,
  TextInputImperativeHandle,
} from '@opallabs/lapidary'
import { opalObjectByUrl } from '@opallabs/routing'
import { getMarkText } from '../../utils'
import * as styles from './styles'
import { EditViewProps } from './types'

/**
 * Multiple extensions can be "link" style objects, this hook will help
 * parse the relevant attributes for the modal from multiple sources.
 */
const useLinkProperties = (editor: Editor) => {
  const [href, setHref] = useState<string>(() => {
    return (
      editor.getAttributes('link').href ??
      editor.getAttributes('embeddedResource').href ??
      ''
    )
  })

  const [label, setLabel] = useState<string>(() => {
    return (
      editor.getAttributes('embeddedResource').label ??
      getMarkText(editor, 'link')
    )
  })

  return {
    href,
    setHref,
    label,
    setLabel,
    normalizedHref: useLinkFormatter(href),
  }
}

export const EditView: VFC<EditViewProps> = ({ editor, onClose }) => {
  const theme = useTheme()
  const hrefInputRef = useRef<TextInputImperativeHandle | null>(null)

  const isActive = useEditorState({
    editor,
    selector: (snapshot) => ({ link: snapshot.editor.isActive('link') }),
  })

  const { href, setHref, label, setLabel, normalizedHref } =
    useLinkProperties(editor)

  const handleLinkUpdate = (e: FormEvent) => {
    e.preventDefault()

    const opalObject = opalObjectByUrl(new URL(normalizedHref).pathname)

    if (opalObject && !label) {
      const existingEmbedType =
        editor.getAttributes('embeddedResource').embedType
      const embedType =
        existingEmbedType || (opalObject.type === 'content' ? 'card' : 'inline')

      editor
        .chain()
        .extendMarkRange('link')
        .setEmbeddedResource({
          href: normalizedHref,
          objectId: opalObject.id,
          objectType: opalObject.type,
          embedType: embedType,
          label: label || normalizedHref,
        })
        .run()
    } else {
      editor
        .chain()
        .extendMarkRange('link')
        .setLink({ href: normalizedHref })
        .command(({ tr }) => {
          tr.insertText(label || normalizedHref)
          return true
        })
        .run()
    }

    onClose()
  }

  const validateUrl = (url: string) => {
    const valid = isUrl(url, { lenient: true })
    return {
      errorMessage: !valid ? 'Please enter a valid URL' : undefined,
      valid,
    }
  }

  // Focus URL input on mount
  useLayoutEffect(() => hrefInputRef.current?.focus(), [])

  return (
    <form onSubmit={handleLinkUpdate} css={styles.editView}>
      <div css={styles.content}>
        <TextInputWithLabel
          ref={hrefInputRef}
          type={TextInputType.Text}
          labelText="URL"
          initialValue={href}
          onChange={setHref}
          required
          styleOverrides={{ container: styles.inputContainer }}
          validator={validateUrl}
        />
        <TextInputWithLabel
          type={TextInputType.Text}
          labelText="Label"
          initialValue={label}
          onChange={setLabel}
          styleOverrides={{ container: styles.inputContainer }}
        />
      </div>

      <div css={styles.actions({ theme })}>
        <Button
          buttonText="Cancel"
          variant="ghost"
          name="cancel"
          type="button"
          onClick={onClose}
        />
        <Button
          buttonText={isActive.link ? 'Save' : 'Insert'}
          variant="primary"
          name="save"
          type="submit"
        />
      </div>
    </form>
  )
}
